/**
 * Signup attribution helper.
 *
 * Captures where a new signup came from so the admin dashboard can see
 * which marketing channel each user arrived through. Priority:
 *   1. `?ref=` query param  (preferred — short slugs like "final-call-reel")
 *   2. `?utm_source` / `?utm_medium` / `?utm_campaign`
 *   3. document.referrer host (e.g. "instagram.com" → "ig-organic")
 *   4. "direct" fallback
 *
 * The captured value persists in sessionStorage so a user who arrives via
 * a QR link, taps around, then signs up later in the same tab is still
 * attributed correctly.
 */

const STORAGE_KEY = "cc_signup_source";
const REFERRER_KEY = "cc_signup_referrer";

function readUrlParams(search) {
  try {
    return new URLSearchParams(search || (typeof window !== "undefined" ? window.location.search : ""));
  } catch {
    return new URLSearchParams("");
  }
}

function hostToChannel(host = "") {
  const h = host.toLowerCase();
  if (!h) return null;
  if (h.includes("instagram.com")) return "ig-organic";
  if (h.includes("facebook.com") || h.includes("fb.com") || h.includes("m.facebook")) return "fb-organic";
  if (h.includes("whatsapp")) return "wa-organic";
  if (h.includes("tiktok")) return "tiktok-organic";
  if (h.includes("youtube") || h.includes("youtu.be")) return "youtube";
  if (h.includes("google.")) return "google-search";
  if (h.includes("bing.com")) return "bing-search";
  if (h.includes("duckduckgo")) return "ddg-search";
  if (h.includes("reddit.com")) return "reddit";
  return null;
}

/**
 * Detect + persist the signup source. Call from any landing point that
 * could become a signup (e.g. Register page mount, Landing page mount).
 */
export function captureSignupSource() {
  if (typeof window === "undefined") return;
  const params = readUrlParams(window.location.search);
  const refSlug = (params.get("ref") || "").trim();
  const utmSource = (params.get("utm_source") || "").trim();
  const utmMedium = (params.get("utm_medium") || "").trim();
  const utmCampaign = (params.get("utm_campaign") || "").trim();

  let source = null;
  if (refSlug) {
    source = `ref:${refSlug}`.slice(0, 80);
  } else if (utmSource) {
    source = [utmSource, utmMedium, utmCampaign].filter(Boolean).join(":").slice(0, 80);
  } else {
    const refHost = (() => {
      try { return new URL(document.referrer).host; } catch { return ""; }
    })();
    const ch = hostToChannel(refHost);
    if (ch) source = ch;
  }

  if (!source) return; // Don't overwrite a previously-captured source with "direct"

  try {
    // First capture wins — don't overwrite if we've already attributed this tab.
    if (!window.sessionStorage.getItem(STORAGE_KEY)) {
      window.sessionStorage.setItem(STORAGE_KEY, source);
      if (document.referrer) {
        window.sessionStorage.setItem(REFERRER_KEY, document.referrer.slice(0, 400));
      }
    }
  } catch { /* storage may be blocked */ }
}

/**
 * Read the captured source + referrer. Returns `{ source, referrer }` with
 * `source` defaulting to "direct" when nothing was captured.
 */
export function getSignupSource() {
  if (typeof window === "undefined") return { source: "direct", referrer: null };
  let source = "direct";
  let referrer = null;
  try {
    source = window.sessionStorage.getItem(STORAGE_KEY) || "direct";
    referrer = window.sessionStorage.getItem(REFERRER_KEY) || null;
  } catch { /* storage blocked — fall through */ }
  // If still default, fall back to document.referrer host check at submit time.
  if (source === "direct" && typeof document !== "undefined" && document.referrer) {
    try {
      const ch = hostToChannel(new URL(document.referrer).host);
      if (ch) {
        source = ch;
        referrer = document.referrer.slice(0, 400);
      }
    } catch { /* ignore */ }
  }
  return { source, referrer };
}

/** Clear after a successful signup so a returning visitor starts clean. */
export function clearSignupSource() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(REFERRER_KEY);
  } catch { /* ignore */ }
}
